import { ATTR_KEYS, type AttrKey } from '../../core/ratings';

/**
 * Leaderboard catalog (ux.md "Leaderboard", stats.md section 7). The server ranks, the client
 * formats by `kind`. Percentages need a minimum of games and attempts, stated in the qualifier.
 */

export type LeaderboardKind = 'rating' | 'elo' | 'avg' | 'pct' | 'count';

export interface LeaderboardStat {
  id: string;
  kind: LeaderboardKind;
  /** The stat family in aggregate.ts, when it is a box-score stat. */
  family?: string;
}

export const ATTR_STAT_PREFIX = 'attr_';

export const LEADERBOARD_STATS: readonly LeaderboardStat[] = [
  { id: 'ovr', kind: 'rating' },
  { id: 'ppg', kind: 'avg', family: 'ppg' },
  { id: 'tpm', kind: 'avg', family: 'tpm' },
  { id: 'fg_pct', kind: 'pct', family: 'fg_pct' },
  { id: 'tp_pct', kind: 'pct', family: 'tp_pct' },
  { id: 'ft_pct', kind: 'pct', family: 'ft_pct' },
  { id: 'wins', kind: 'count' },
  { id: 'win_pct', kind: 'pct' },
  { id: 'streak', kind: 'count' },
  { id: 'rpg', kind: 'avg', family: 'rpg' },
  { id: 'apg', kind: 'avg', family: 'apg' },
  { id: 'spg', kind: 'avg', family: 'spg' },
  { id: 'bpg', kind: 'avg', family: 'bpg' },
  { id: 'elo', kind: 'elo' },
  { id: 'gp', kind: 'count' },
  ...ATTR_KEYS.map((k): LeaderboardStat => ({ id: `${ATTR_STAT_PREFIX}${k}`, kind: 'rating' })),
];

export const LEADERBOARD_IDS = LEADERBOARD_STATS.map((s) => s.id);

export function leaderboardStat(id: string): LeaderboardStat | undefined {
  return LEADERBOARD_STATS.find((s) => s.id === id);
}

export function attrOfStat(id: string): AttrKey | null {
  if (!id.startsWith(ATTR_STAT_PREFIX)) return null;
  const k = id.slice(ATTR_STAT_PREFIX.length);
  return (ATTR_KEYS as readonly string[]).includes(k) ? (k as AttrKey) : null;
}

/** Minimums for a percentage to rank (the footnote under the board). */
export const QUALIFY = {
  /** Games with misses tracked. */
  games: 3,
  attempts: { fg_pct: 20, tp_pct: 10, ft_pct: 10 } as Record<string, number>,
  /** win_pct needs this many games. */
  winPctGames: 3,
} as const;

/** The 30-day window in ms. */
export const WINDOW_30D_MS = 30 * 86_400_000;
